import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { AskAiHost, CopilotBubble, useAskAiState } from '../../features/copilot';
import { Composer } from '../../features/mail';
import { AssignAiHost } from '../../features/tasks';
import { cn } from '../../lib/cn';
import { ScopeProvider } from '../../lib/scope';
import { useAccess } from '../../lib/useAccess';
import { ListSkeleton } from '../../ui/States';
import { type AddDialogState, AddDialogs } from './AddDialogs';
import { CommandPalette } from './CommandPalette';
import { MobileMenu, useMobileMenuHistory } from './MobileMenu';
import { MobileNav } from './MobileNav';
import type { AddDialogKind } from './nav';
import { OfflineBanner } from './OfflineBanner';
import { useWorkspaceLinkGate } from './other-workspaces';
import { Sidebar } from './Sidebar';
import { type AddDialogDefaults, type ComposeDefaults, type ShellActions, ShellContext } from './shell-context';

/**
 * Szkielet aplikacji (DESIGN §4): desktop — białe menu boczne 232 px + treść na szarym tle (bg/app), bez górnego paska;
 * mobile (< 768 px) — bez menu bocznego, dolne menu. Wyszukiwanie i polecenia pod ⌘K / Ctrl+K.
 * Fala 4: panel „Zapytaj AI” (`AskAiHost`, otwierany z ⌘K, Dziś i widoków) i kompozytor „Napisz do: …” z ⌘K.
 * Fala 5: „Zleć Claude dla zadania…” z ⌘K (`AssignAiHost`: dialog „Zleć Claude” dla wybranego zadania; Fala 9).
 * Fala 10: bąbel AI (`CopilotBubble`) w prawym dolnym rogu każdego ekranu (telefon: nad dolnym menu), ⌘J / Ctrl+J;
 * czat dokowany z prawej — od 1600 px treść zwęża się o szerokość panelu (nic nie jest zasłonięte; zostaje ≥ 948 px,
 * jak na ekranie 1180 px — węższa treść łamała tabele list projektowane pod szerokość okna), węższy desktop: panel nad
 * prawą częścią treści bez przyciemnienia. Telefon: większy dolny margines treści (bąbel nad menu).
 * 17.10.2026 (DECISIONS [P] „Telefon: łatwy dostęp do całego menu”): na telefonie dolne menu Dziś · Inbox · mikrofon ·
 * Zadania · Menu; „Menu” otwiera pełnoekranowy panel `MobileMenu` (wszystkie moduły, wyszukiwanie, workspace'y,
 * konto) — zastąpił przycisk konta w prawym górnym rogu i menu boczne w szufladzie. Bąbel AI na telefonie: 44 px
 * i chowa się przy przewijaniu w dół (`useHideOnScrollDown`), żeby nie zasłaniać akcji wierszy list (np. „Więcej” wpisu
 * czasu przy prawej krawędzi); wraca przy przewijaniu w górę, na górze i na końcu strony (dolny margines treści).
 */
export function AppShell() {
  const access = useAccess();
  const chatOpen = useAskAiState().open;
  // link do innego workspace'u (?ws=<slug>): najpierw przełączenie, potem strona
  const linkPending = useWorkspaceLinkGate();
  const [palette, setPalette] = useState<{ open: boolean; mode: 'all' | 'add' }>({ open: false, mode: 'all' });
  // panel Menu na telefonie = wpis w historii (Wstecz zamyka); przejście z panelu zastępuje ten wpis
  const { open: mobileMenuOpen, openMenu: openMobileMenu, closeMenu: closeMobileMenu } = useMobileMenuHistory();
  const [addDialog, setAddDialog] = useState<AddDialogState | null>(null);
  const [compose, setCompose] = useState<ComposeDefaults | null>(null);
  const bubbleHidden = useHideOnScrollDown();

  const openCommandPalette = useCallback((mode: 'all' | 'add' = 'all') => setPalette({ open: true, mode }), []);
  const openAddDialog = useCallback(
    (kind: AddDialogKind, defaults: AddDialogDefaults = {}) => setAddDialog({ kind, defaults }),
    [],
  );
  const openComposer = useCallback((defaults: ComposeDefaults) => setCompose(defaults), []);
  const actions = useMemo<ShellActions>(
    () => ({ openCommandPalette, openMobileMenu, mobileMenuOpen, openAddDialog, openComposer }),
    [openCommandPalette, openMobileMenu, mobileMenuOpen, openAddDialog, openComposer],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPalette((p) => ({ open: !p.open, mode: 'all' }));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <ShellContext.Provider value={actions}>
      <ScopeProvider enabled={access.isManagerOrAbove}>
        <a
          href="#tresc"
          className="sr-only z-50 rounded-md bg-canvas px-3 py-2 text-label text-accent focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
        >
          Przejdź do treści
        </a>
        <div className={cn('min-h-dvh bg-app md:flex', chatOpen && 'min-[1600px]:pr-[420px]')}>
          {/* menu boczne: biała kolumna na całą wysokość z linią border/subtle po prawej */}
          <aside className="hidden shrink-0 border-r border-line bg-panel md:block md:w-[232px]">
            <div className="sticky top-0 h-dvh">
              <Sidebar className="w-full" />
            </div>
          </aside>
          <main
            id="tresc"
            tabIndex={-1}
            className="min-w-0 flex-1 pb-[calc(136px+env(safe-area-inset-bottom))] focus:outline-none md:pb-0"
          >
            <OfflineBanner />
            {linkPending ? (
              <div className="px-4 pt-6 md:px-8" aria-busy="true">
                <ListSkeleton rows={4} />
              </div>
            ) : (
              <Outlet />
            )}
          </main>
          <MobileNav className="md:hidden" />
        </div>

        <MobileMenu open={mobileMenuOpen} onOpenChange={(open) => (open ? openMobileMenu() : closeMobileMenu())} />

        <CommandPalette
          open={palette.open}
          mode={palette.mode}
          onOpenChange={(open) => setPalette((p) => ({ ...p, open }))}
        />
        <AddDialogs state={addDialog} onClose={() => setAddDialog(null)} />
        {compose && access.can('inbox', 'edit') && (
          <Composer
            open
            onOpenChange={(open) => !open && setCompose(null)}
            clientId={compose.clientId}
            defaults={{ to: compose.to }}
            title="Nowa wiadomość"
          />
        )}
        <AskAiHost />
        <CopilotBubble
          className={cn(
            // telefon: 44 px (pole dotyku) i chowanie przy przewijaniu w dół; desktop bez zmian
            'max-md:size-11 max-md:transition-[translate,opacity,visibility,box-shadow,background-color] max-md:duration-200',
            bubbleHidden &&
              'max-md:pointer-events-none max-md:invisible max-md:translate-x-[calc(100%+16px)] max-md:opacity-0',
          )}
        />
        <AssignAiHost />
      </ScopeProvider>
    </ShellContext.Provider>
  );
}

/**
 * Telefon: `true`, gdy osoba przewija stronę w dół (bąbel AI chowa się, żeby nie zasłaniać akcji wierszy przy prawej
 * krawędzi). `false` przy przewijaniu w górę, blisko góry, na końcu strony (tam dolny margines treści i tak trzyma
 * treść nad bąblem) i po zmianie strony.
 */
export function useHideOnScrollDown(): boolean {
  const [hidden, setHidden] = useState(false);
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: reakcja na zmianę strony
  useEffect(() => setHidden(false), [pathname]);

  useEffect(() => {
    let last = window.scrollY;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        const atEnd = y + window.innerHeight >= document.documentElement.scrollHeight - 24;
        const delta = y - last;
        if (y < 64 || atEnd || delta < -8) {
          setHidden(false);
          last = y;
        } else if (delta > 8) {
          setHidden(true);
          last = y;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  return hidden;
}
